import { Badge, Button, Checkbox, Col, Drawer, Flex, Input, List, Row, Space, Spin, Tabs, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { BsTags } from 'react-icons/bs';
import { CgNotes } from 'react-icons/cg';
import { GoChecklist } from 'react-icons/go';
import { HiExternalLink } from 'react-icons/hi';
import { ImAttachment } from 'react-icons/im';
import { IoClose, IoSend } from 'react-icons/io5';
import { LuCircleDot, LuLink, LuUserCheck, LuUsers } from 'react-icons/lu';
import { TbCalendarDue, TbFileDescription } from 'react-icons/tb';
import { useParams } from 'react-router-dom';
import { TASk_TABS } from '../../constants';
import { isAdmin, isManager, isStaff, isSuperAdmin, priorityWithIcon, progressWithIcon } from '../../helper';
import { decodeId } from '../../helper/hashids';
import { createComment, getTaskComments } from '../../redux/actions/comments';
import { getTasks, updateTask } from '../../redux/actions/tasks';
import { getFileUrl } from '../../utils';
import ChecklistProgress from '../ChecklistProgress';
import TaskActivities from '../TaskActivities';
import UploadAttachment from '../UploadAttachment';
import UserAvatar from '../UserAvatar';

const TaskDetailView = ({ open, onClose, restaurant, task, isView = false, showTabs = true }) => {
    const { id: taskId } = useParams();
    const id = decodeId(taskId);

    const {
        title,
        progress,
        due_date,
        assignees,
        labels,
        notes,
        files,
        links,
        checklist,
        completion_notes,
        completion_files,
        completed_by_name,
        completed_at,
    } = task;
    const attachments = files && JSON.parse(files);
    const taskLinks = links && JSON.parse(links);
    const completionAttachments = completion_files && JSON.parse(completion_files);
    const [trackCheckList, setChecklist] = useState(checklist ?? []);
    const [removeAssignee, setRemoveAssignee] = useState(task.assignee_ids ?? []);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');

    const [activeTab, setActiveTab] = useState('1');

    const fetchComments = async () => {
        if (task?.id) {
            setIsFetching(true);
            const res = await getTaskComments(task.id);
            if (res?.ack) {
                setComments(res.data);
            }
            setIsFetching(false);
        }
    };

    useEffect(() => {
        if (showTabs && open && task?.id && activeTab === '1') {
            fetchComments();
        }
    }, [showTabs, open, task?.id, activeTab]);

    const handleAddComment = async () => {
        if (!newComment.trim() || isSubmitting) return;

        setIsSubmitting(true);
        const res = await createComment(task.id, { comment: newComment });
        if (res?.ack) {
            setComments((prev) => [res.data, ...prev]);
            setNewComment('');
        }
        setIsSubmitting(false);
    };

    const isArchived = progress === 2;
    const isCompleted = progress === 1;

    const handleDeleteAssignee = (id) => {
        setRemoveAssignee((prev) => _.without(prev, id));
    };

    const assigneeTags = assignees?.filter(user => removeAssignee.includes(user.id)).map((user) => {
        const isClosable = !isView && (isSuperAdmin() || isAdmin() || isManager() || (isStaff() && !!restaurant?.allow_staff_create_task));
        return (
            <div
                key={user?.id}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px 2px 2px',
                    background: '#f5f5f5',
                    borderRadius: '20px',
                    gap: '8px',
                    border: '1px solid #e8e8e8'
                }}
            >
                <UserAvatar size="small" user={user} />
                <Typography.Text style={{ fontSize: '14px', color: '#1a1a1a', fontWeight: 500 }}>
                    {`${user?.first_name} ${user?.last_name ?? ''}`}
                </Typography.Text>
                {isClosable && (
                    <IoClose
                        size={16}
                        style={{ color: '#8c8c8c', cursor: 'pointer', marginLeft: '-2px' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAssignee(user?.id);
                        }}
                    />
                )}
            </div>
        );
    });

    const fileList = attachments?.map((file) => {
        return {
            uid: file,
            name: file,
            status: 'done',
            url: getFileUrl('tasks', task?.id, file),
            payload: true,
        };
    });

    const completionFileList = completionAttachments?.map((file) => {
        return {
            uid: file,
            name: file,
            status: 'done',
            url: getFileUrl('tasks', task?.id, file),
            payload: true,
        };
    });

    const labelTags = labels?.map((label) => {
        return (
            <Tag key={label?.label_id} color={label?.color}>
                {label?.title}
            </Tag>
        );
    });

    const handleCheckboxChange = (id) => {
        const updatedChecklist = trackCheckList?.map((item) =>
            item.checklist_id === id ? { ...item, status: item.status === 1 ? 0 : 1 } : item
        );

        setChecklist(updatedChecklist);
    };

    const checkListOptions = (
        <div>
            <ChecklistProgress trackCheckList={trackCheckList} justify="end" />
            <div>
                {trackCheckList?.map((item) => (
                    <div key={item.checklist_id}>
                        <Input
                            value={item.title}
                            placeholder="Add an item"
                            variant="borderless"
                            style={{ flex: 1, marginRight: '8px' }}
                            disabled={isArchived}
                            readOnly
                            prefix={
                                <Checkbox
                                    checked={item.status}
                                    onChange={() => handleCheckboxChange(item.checklist_id)}
                                    style={{ marginRight: '8px' }}
                                    disabled={isArchived || isView}
                                />
                            }
                        />
                    </div>
                ))}
            </div>
        </div>
    );

    const taskMeta = [
        {
            icon: <LuCircleDot />,
            title: 'Status',
            value: (
                <div style={{ display: 'inline-flex' }}>
                    {isArchived ? <Badge status="default" text="Archived" /> : progressWithIcon(progress)}
                </div>
            ),
        },
        {
            icon: <TbCalendarDue />,
            title: 'Due Date',
            value: due_date && (
                <Typography.Text strong style={{ fontSize: '15px' }}>
                    {dayjs(due_date).format('DD/MM/YYYY HH:mm')}
                </Typography.Text>
            ),
            hide: !due_date,
        },
        {
            icon: <LuUsers />,
            title: 'Assignees',
            value: (
                <Flex wrap gap={6}>
                    {assigneeTags}
                </Flex>
            ),
            hide: !assigneeTags?.length,
        },
        {
            icon: <BsTags />,
            title: 'Labels',
            value: (
                <Flex wrap gap={6}>
                    {labelTags}
                </Flex>
            ),
            hide: !labelTags?.length,
        },
    ];

    const taskContent = [
        {
            icon: <TbFileDescription />,
            title: 'Notes',
            value: notes ? (
                <div style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0',
                    background: '#fafafa'
                }}>
                    <Typography.Text style={{ color: '#434343', whiteSpace: 'pre-wrap' }}>
                        {notes}
                    </Typography.Text>
                </div>
            ) : null,
            hide: !notes,
        },
        {
            icon: <GoChecklist />,
            title: 'Checklist',
            value: (
                <div style={{
                    padding: '4px 0'
                }}>
                    {checkListOptions}
                </div>
            ),
            hide: !checklist?.length,
        },
        {
            icon: <ImAttachment style={{ color: '#1677ff' }} />,
            title: 'Attachments',
            value: <UploadAttachment readOnly fileList={fileList} />,
            hide: !attachments?.length,
        },
        {
            icon: <LuLink style={{ color: '#1677ff' }} />,
            title: 'Links',
            value: (
                <Flex horizontal gap={8} wrap>
                    {taskLinks?.map((link, idx) => (
                        <Button
                            key={idx}
                            type="default"
                            size="small"
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                borderRadius: '16px',
                                background: '#f0f5ff',
                                borderColor: '#adc6ff',
                                color: '#1d39c4',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            {link.title}
                            <HiExternalLink style={{ fontSize: '14px' }} />
                        </Button>
                    ))}
                </Flex>
            ),
            hide: !task.links?.length,
        },
    ];

    const completionInfo = isCompleted ? [
        {
            icon: <CgNotes style={{ color: '#52c41a' }} />,
            title: 'Completion Notes',
            value: completion_notes ? (
                <div style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid #d9f7be',
                    background: '#f6ffed'
                }}>
                    <Typography.Text style={{ color: '#237804', whiteSpace: 'pre-wrap' }}>
                        {completion_notes}
                    </Typography.Text>
                </div>
            ) : null,
            hide: !completion_notes,
        },
        {
            icon: <ImAttachment />,
            title: `Completion Files`,
            value: <UploadAttachment readOnly fileList={completionFileList} />,
            hide: !completionFileList?.length,
        },
        {
            icon: <LuUserCheck />,
            title: 'Completed By',
            value: (
                <Space>
                    <Typography.Text strong>{completed_by_name}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        at {completed_at && dayjs(completed_at).format('DD/MM/YYYY HH:mm')}
                    </Typography.Text>
                </Space>
            ),
            hide: !completed_by_name,
        },
    ] : [];

    const labelsIds = labels.map((el) => el?.label_id)
    const handleClose = async () => {
        const isSame = _.isEqual(trackCheckList, task.checklist);
        const isAssigneeSame = _.isEqual(removeAssignee, task.assignee_ids);

        if (!isSame || !isAssigneeSame) {
            const body = {
                ...task,
                labels: labelsIds,
                checklist: trackCheckList,
                assignees: removeAssignee,
            };
            const res = await updateTask(task.id, body, { toast: false });
            res?.ack && getTasks({ id, filter: TASk_TABS.PENDING });
        }

        onClose();
    };

    return (
        <Drawer
            size="large"
            onClose={handleClose}
            open={open}
            title={
                <Typography.Text type="secondary" style={{ fontSize: '14px', fontWeight: 400 }}>
                    {restaurant?.title} / {isArchived ? 'Archived' : progress === 1 ? 'Completed' : 'Pending'}
                </Typography.Text>
            }
            extra={priorityWithIcon(task?.priority)}
            className="task-detail"
            styles={{
                header: { borderBottom: '1px solid #f0f0f0', padding: '16px 24px' },
                body: { padding: '24px' },
                footer: { padding: 0 }
            }}
            footer={showTabs && activeTab === '1' && !isCompleted && !isArchived && (
                <div style={{
                    padding: '12px 24px',
                    background: '#ffffff',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '12px',
                }}>
                    <Input.TextArea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment"
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        style={{
                            borderRadius: '8px',
                            background: '#f9f9f9',
                            border: 'none',
                            padding: '8px 12px'
                        }}
                        onPressEnter={(e) => {
                            if (!e.shiftKey && newComment.trim()) {
                                e.preventDefault();
                                handleAddComment();
                            }
                        }}
                    />
                    <Button
                        type="primary"
                        shape="circle"
                        loading={isSubmitting}
                        icon={!isSubmitting && <IoSend style={{ fontSize: '18px', marginLeft: '2px' }} />}
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                        style={{
                            width: '40px',
                            height: '40px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    />
                </div>
            )}
        >
            <Typography.Title level={3} style={{ marginBottom: 24, marginTop: 0 }}>
                {title}
            </Typography.Title>

            <div style={{
                marginBottom: 20,
                padding: '16px 20px',
                background: '#f9f9f9',
                borderRadius: '12px',
                border: '1px solid #f0f0f0'
            }}>
                <Row gutter={[32, 24]}>
                    {taskMeta.map((item, index) => !item.hide && (
                        <Col span={24} key={index}>
                            <Flex align="start" gap={16}>
                                <div style={{
                                    width: 120,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    color: '#262626',
                                    fontWeight: 600,
                                    paddingTop: '2px'
                                }}>
                                    <span style={{ display: 'flex', color: '#1677ff', fontSize: '20px' }}>
                                        {item.icon}
                                    </span>
                                    <span style={{ fontSize: '15px' }}>{item.title}</span>
                                </div>
                                <div style={{ flex: 1, minHeight: '28px', display: 'flex', alignItems: 'center' }}>
                                    <div style={{ fontSize: '15px', color: '#000000' }}>
                                        {item.value}
                                    </div>
                                </div>
                            </Flex>
                        </Col>
                    ))}
                </Row>
            </div>

            <div className="task-content">
                {taskContent.map(({ icon, title, value, hide }, index) => !hide && (
                    <div key={index} style={{ marginBottom: 16 }}>
                        <Typography.Title level={5} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {icon} {title}
                        </Typography.Title>
                        <div style={{ paddingLeft: '0' }}>
                            {value}
                        </div>
                    </div>
                ))}
            </div>

            {isCompleted && !!completionInfo.length && (
                <div style={{ marginTop: 32, marginBottom: 24 }}>
                    {completionInfo.map(({ icon, title, value, hide }, index) => !hide && (
                        <div key={index} style={{ marginBottom: 24 }}>
                            <Typography.Title level={5} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {icon} {title}
                            </Typography.Title>
                            <div style={{ paddingLeft: '0' }}>
                                {value}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showTabs && (
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    size="large"
                    items={[
                        {
                            key: '1',
                            label: (
                                <Space size={4}>
                                    <span>Comments</span>
                                    <Badge
                                        count={comments.length}
                                        style={{
                                            backgroundColor: '#f0f0f0',
                                            color: '#595959',
                                            boxShadow: 'none',
                                            fontSize: '11px'
                                        }}
                                    />
                                </Space>
                            ),
                            children: (
                                <div style={{ padding: '24px 16px', minHeight: '200px' }}>
                                    {isFetching ? (
                                        <Flex justify="center" align="center" style={{ padding: '60px 0' }}>
                                            <Spin />
                                        </Flex>
                                    ) : comments.length === 0 ? (
                                        <Flex justify="center" align="center" style={{ padding: '60px 0' }}>
                                            <Typography.Text type="secondary">No comments yet</Typography.Text>
                                        </Flex>
                                    ) : (
                                        <List
                                            itemLayout="horizontal"
                                            dataSource={comments}
                                            renderItem={(item) => {
                                                return (
                                                    <div style={{ display: 'flex', gap: '8px', }}>
                                                        <div style={{ paddingTop: '4px' }}>
                                                            <UserAvatar user={item.user} size="small" />
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <Flex justify="space-between" align="center" style={{ marginBottom: '6px' }}>
                                                                <Typography.Text strong style={{ fontSize: '14px', color: '#1a1a1a' }}>
                                                                    {`${item.user?.first_name} ${item.user?.last_name ?? ''}`}
                                                                </Typography.Text>
                                                            </Flex>
                                                            <div style={{
                                                                padding: '12px 16px',
                                                                background: '#f9f9f9',
                                                                borderRadius: '0 12px 12px 12px',
                                                                border: '1px solid #f0f0f0',
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                                            }}>
                                                                <Typography.Text style={{ color: '#434343', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                                                    {item.comment}
                                                                </Typography.Text>
                                                            </div>
                                                            <div style={{ marginTop: '4px', textAlign: 'right' }}>
                                                                <Typography.Text type="secondary" style={{ fontSize: '11px' }}>
                                                                    {dayjs(item.createdAt).format('DD/MM/YYYY HH:mm')}
                                                                </Typography.Text>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        />
                                    )}
                                </div>
                            ),
                        },
                        (isSuperAdmin() || isAdmin() || isManager()) && {
                            key: '2',
                            label: (
                                <Space size={4}>
                                    <span>Activity</span>
                                </Space>
                            ),
                            children: (
                                <TaskActivities taskId={task.id} activeTab={activeTab} />
                            ),
                        },
                    ].filter(Boolean)}
                />
            )}
        </Drawer>
    );
};

export default TaskDetailView;
